import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ShieldCheck,
  FileText,
  RotateCcw,
  Headphones,
  ExternalLink,
  Wallet,
  Zap,
  ArrowRight,
} from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";
import { useAuth } from "@/context/AuthContext";

export function MuceptBadge({ className = "" }) {
  return (
    <a
      href="https://mucept.com"
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-50 hover:bg-white border border-slate-200/80 hover:border-slate-300 shadow-2xs hover:shadow-xs transition-all duration-200 active:scale-95 shrink-0 ${className}`}
      data-testid="footer-mucept-link"
      title="Mucept"
    >
      <span className="text-xs font-normal text-slate-500 group-hover:text-slate-700 transition-colors">
        A product of
      </span>
      <img
        src="/mucept-logo.jpeg"
        alt="mucept"
        className="h-4 sm:h-4.5 w-auto object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-200"
      />
    </a>
  );
}

export default function Footer({ showCta = false, className = "" }) {
  const { settings } = usePlatform();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const appTitle = settings?.title || "GEMS";
  const isLoggedIn = Boolean(user && user.id);

  const handleHashClick = (e, hashId) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.getElementById(hashId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      navigate(`/#${hashId}`);
    }
  };

  const handleLogoClick = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer className={`bg-white text-slate-600 border-t border-slate-200/60 ${className}`}>
      {/* Top CTA Banner - rendered only when showCta is true and user is not logged in */}
      {showCta && !isLoggedIn && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white p-8 sm:p-12 lg:p-14 text-center shadow-xl shadow-blue-500/10">
            {/* Ambient subtle glow background shapes */}
            <div className="absolute -top-24 -left-24 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-indigo-400/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-2xl mx-auto flex flex-col items-center">
              {/* Lightning Bolt Badge */}
              <div className="w-12 h-12 rounded-2xl bg-amber-400/20 border border-amber-300/30 grid place-items-center mb-5 backdrop-blur-md shadow-inner">
                <Zap className="w-6 h-6 text-amber-300 fill-amber-300/40" />
              </div>

              <h2 className="heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
                Ready to continue managing your expenses?
              </h2>

              <p className="mt-3 text-blue-100 text-xs sm:text-sm max-w-lg leading-relaxed">
                Access your shared groups, balances, expense ledgers, and financial reports seamlessly for your workspace.
              </p>

              <button
                onClick={() => navigate("/workspace")}
                className="mt-6 sm:mt-8 inline-flex items-center gap-2.5 px-6 sm:px-8 py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm sm:text-base shadow-lg shadow-orange-500/30 hover:shadow-orange-500/40 transition-all duration-200 active:scale-95"
                data-testid="footer-cta-btn"
              >
                <span>Open App Workspace</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Multi-Column Footer Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Column 1: Brand & Tagline */}
          <div className="lg:col-span-5 space-y-4">
            <Link to="/" onClick={handleLogoClick} className="inline-flex items-center gap-2.5" data-testid="footer-brand-logo">
              <div className="w-9 h-9 rounded-2xl bg-slate-900 grid place-items-center overflow-hidden shadow-xs">
                <Wallet className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="heading font-bold text-slate-900 text-xl tracking-tight">{appTitle}</span>
            </Link>

            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed max-w-md">
              Group expense ledger & bill splitting platform that helps you run your entire shared finances from one place. Streamline settlements, track expenses, and manage balances effortlessly.
            </p>
          </div>

          {/* Column 2: Product Links */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-900">Product</h3>
            <ul className="space-y-2 text-xs font-medium">
              <li>
                <a href="#features" onClick={(e) => handleHashClick(e, "features")} className="hover:text-slate-900 transition-colors">Features</a>
              </li>
              <li>
                <a href="#how" onClick={(e) => handleHashClick(e, "how")} className="hover:text-slate-900 transition-colors">How it works</a>
              </li>
              <li>
                <a href="#security" onClick={(e) => handleHashClick(e, "security")} className="hover:text-slate-900 transition-colors">Ledger</a>
              </li>
              <li>
                <Link to={isLoggedIn ? "/workspace" : "/login"} className="hover:text-slate-900 transition-colors">
                  {isLoggedIn ? "Go to Workspace" : "Sign In"}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Legal & Support */}
          <div className="lg:col-span-3 space-y-3">
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-900">Legal & Support</h3>
            <ul className="space-y-2.5 text-xs font-medium">
              <li>
                <Link
                  to="/privacy-policy"
                  className="inline-flex items-center gap-2 hover:text-slate-900 transition-colors group"
                  data-testid="footer-privacy-link"
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Privacy Policy</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="inline-flex items-center gap-2 hover:text-slate-900 transition-colors group"
                  data-testid="footer-terms-link"
                >
                  <FileText className="w-3.5 h-3.5 text-blue-600" />
                  <span>Terms & Conditions</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/refund-policy"
                  className="inline-flex items-center gap-2 hover:text-slate-900 transition-colors group"
                  data-testid="footer-refund-link"
                >
                  <RotateCcw className="w-3.5 h-3.5 text-amber-600" />
                  <span>Refund Policy</span>
                </Link>
              </li>
              <li>
                <a
                  href="https://saharey.com/contactus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-slate-900 transition-colors group"
                  data-testid="footer-contact-link"
                >
                  <Headphones className="w-3.5 h-3.5 text-indigo-600" />
                  <span>Contact Us</span>
                  <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-slate-700 transition-colors" />
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Workspace CTA Buttons */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-900">Workspace</h3>
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-semibold text-xs transition-colors shadow-xs"
                data-testid="footer-workspace-link"
              >
                Go to Workspace
              </Link>
            ) : (
              <div className="flex flex-col gap-2">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-semibold text-xs transition-colors shadow-xs"
                  data-testid="footer-register-link"
                >
                  Get Started
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center w-full px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs transition-colors"
                  data-testid="footer-login-link"
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Sub-Footer Bar */}
        <div className="mt-12 pt-6 border-t border-slate-200/70 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left text-xs text-slate-500">
          <p className="font-normal">
            Copyright © Saharey Technologies (a Saharey Group Company). All Rights Reserved
          </p>

          <MuceptBadge />
        </div>
      </div>
    </footer>
  );
}

